// QuotePdf.js
import numWords from "num-words";
import {
  loadPdfImages,
  getPdfHeader,
  getPdfFooter,
  PDF_PAGE_MARGINS,
  createAndOpenPdf,
} from "../../../utility/pdfUtils";

import { getCompanyProfile } from "../../../Services/Services";

// ─── Main Export ───
export const generatePdf = async (quote, action = "open") => {
  const { headerBase64, footerBase64 } = await loadPdfImages();
  const company = await getCompanyProfile();


  // ─── Helpers ───
  const formatMoney = (amount) =>
    Number(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const toUpperCaseText = (text) => {
    if (!text) return "";
    return text.toUpperCase();
  };

  const toCamelCase = (str) => {
    if (!str) return "";
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const toTitleCase = (text = "") =>
    text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  // ─── Amount in words ───
  const amount =
    quote.final_agreed_amount > 0
      ? quote.final_agreed_amount
      : quote.total_amount;

  const amountInWords = amount
    ? toCamelCase(numWords(Math.floor(Number(amount))))
    : "Zero";

  // ─── Terms Table ───
  const buildTermsTableBody = () => {
    const body = [];

    if (quote.terms_and_conditions?.length) {
      quote.terms_and_conditions.forEach((tc) => {
        body.push([{ text: tc.key, bold: true }, { text: tc.value }]);
      });
    }

    if (quote.payment_terms?.length) {
      body.push([
        { text: "Payment Terms", bold: true },
        { ol: quote.payment_terms.map((pt) => pt.payment) },
      ]);
    }

    if (quote.scope_of_work?.length) {
      body.push([
        { text: "Scope of Work", bold: true },
        { ol: quote.scope_of_work.map((sw) => sw.scope_of_work) },
      ]);
    }

    if (quote.exclusions?.length) {
      body.push([
        { text: "Exclusions", bold: true },
        { ol: quote.exclusions.map((ex) => ex.exclusions) },
      ]);
    }

    return body;
  };

  const docDefinition = {
    watermark: {
      text: "SIDDHARTH INDUSTRIES",
      color: "#000000",
      opacity: 0.03,
      bold: true,
      fontSize: 65,
      angle: -45,
    },

    pageMargins: PDF_PAGE_MARGINS,

    defaultStyle: { fontSize: 9 },

    header: getPdfHeader(headerBase64),
    footer: getPdfFooter(footerBase64, false),

    content: [
      // ─── Ref & Date ───
      {
        columns: [
          { text: `Ref.No.: ${quote.ref_no}`, style: "header" },
          {
            text: `Date: ${formatDate(quote.date)}`,
            alignment: "right",
            style: "header",
          },
        ],
        margin: [0, 0, 0, 10],
      },

      // ─── To Address ───
      {
        text: [
          { text: "To,\n" },
          `${toTitleCase(quote.to_name)}\n${toTitleCase(
            quote.to_company
          )}\n${toTitleCase(quote.to_address)}`,
        ],
        margin: [0, 0, 0, 10],
        style: "text",
      },

      // ─── Subject ───
      {
        text: `Subject: ${quote.subject}`,
        margin: [0, 0, 0, 10],
        style: "text",
        bold: true,
      },

      // ─── Salutation ───
      { text: "Dear Sir,", margin: [0, 0, 0, 5], style: "text" },

      // ─── Body ───
      {
        text: toCamelCase(quote.body),
        style: "text",
        margin: [0, 0, 0, 10],
      },

      // ─── Items Table - UPDATED AS PER QUOTATION (Voltage Ratio / Rating / Winding / Price) ───
      {
        style: "tableExample",
        table: {
          headerRows: 1,
          widths: ["7%", "23%", "25%", "22%", "23%"],
          body: [
            [
              { text: "S. No.", bold: true, alignment: "center", fillColor: "#f5f5f5" },
              { text: "Voltage Ratio", bold: true, alignment: "center", fillColor: "#f5f5f5" },
              { text: "Rating Specification", bold: true, alignment: "center", fillColor: "#f5f5f5" },
              { text: "Winding Material", bold: true, alignment: "center", fillColor: "#f5f5f5" },
              { text: "Price Per Unit (Rs.)", bold: true, alignment: "center", fillColor: "#f5f5f5" },
            ],
            ...(quote.items_list || quote.items || []).map((item, idx) => {
              // Support both new and old field names
              const voltage = item.voltage_ratio ?? item.voltageRatio ?? item.description ?? item.item_name ?? "";
              const rating = item.rating_specification ?? item.ratingSpecification ?? item.rating ?? "";
              const winding = item.winding_material ?? item.windingMaterial ?? item.unit ?? item.uom ?? "";
              const price = item.price_per_unit ?? item.pricePerUnit ?? item.rate ?? item.amount ?? 0;
              return [
                { text: idx + 1, alignment: "center" },
                { text: toUpperCaseText(voltage), alignment: "center" },
                { text: toUpperCaseText(rating), alignment: "center" },
                { text: toUpperCaseText(winding), alignment: "center" },
                { text: formatMoney(price), alignment: "center" },
              ];
            }),

            // ─── Total Row - Price Per Unit ka sum ───
            [
              {
                text: `TOTAL : ₹ ${formatMoney(quote.total_amount)}\n${quote.final_agreed_amount > 0
                  ? `Final Agreed Amount : ₹ ${formatMoney(quote.final_agreed_amount)}\n`
                  : ""
                  }\nIn Words: ${amountInWords} Only`,
                colSpan: 5,
                alignment: "right",
                bold: true,
                margin: [0, 6, 6, 6],
              },
              {}, {}, {}, {}
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#aaa",
          vLineColor: () => "#aaa",
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
      },

      // ─── Terms Note ───
      {
        text: "The rates are subject to terms and conditions as mentioned below :-",
        margin: [0, 20, 0, 5],
        bold: true,
      },

      // ─── Terms Table ───
      {
        table: {
          widths: ["35%", "65%"],
          body: buildTermsTableBody(),
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          paddingTop: () => 2,
          paddingBottom: () => 2,
          paddingLeft: () => 4,
          paddingRight: () => 4,
        },
        margin: [0, 10, 0, 10],
        fontSize: 8,
      },

      // ─── Closing Note ───
      {
        text: "We trust you will find our offer acceptable. In case you need any further clarification / Confirmation, please feel free to contact us...",
        margin: [0, 10, 0, 10],
        fontSize: 9,
      },

      // ─── Sign Off ───
      {
       text: `Yours Faithfully,\n ${company.company_name || ""},Raipur`,
        bold: true,
        margin: [0, 10, 0, 2],
        fontSize: 9,
      },

      
    ],

    styles: {
      header: { fontSize: 9, bold: true },
      text: { fontSize: 9 },
      tableExample: { fontSize: 9, margin: [0, 10, 0, 10] },
    },
  };

  createAndOpenPdf(
    docDefinition,
    action,
    `Quote_${quote?.ref_no || "export"}`
  );
};
